import { useCallback, useEffect, useState } from 'react'
import { Check, Clock } from 'lucide-react'
import { adminApi, unwrap } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useSocket } from '../hooks/useSocket'
import { useModalA11y } from '../hooks/useModalA11y'
import EmptyState from '../components/shared/EmptyState'
import ErrorState from '../components/shared/ErrorState'

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface AgentCash {
  agent_id: string
  agent_name: string
  collected: number
  deposited: number
  pending: number
  orders_count: number
}

interface CashSummary {
  date: string
  total_collected: number
  total_deposited: number
  pending_deposit: number
  by_agent: AgentCash[]
}

interface CashRecord {
  id: string
  agent_id: string
  order_id: string
  amount: number
  deposited: boolean
  deposited_at: string | null
  deposited_method: string | null
  notes: string | null
  created_at: string
  agent_name?: string
  order_number?: string
  customer_name?: string
}

interface CashHistoryPayload {
  records?: CashRecord[]
  collections?: CashRecord[]
  total?: number
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0))

const formatTime = (value: string) => {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const getToday = () => new Date().toISOString().slice(0, 10)

const DEPOSIT_METHODS = [
  { key: 'cash_handover', label: 'Cash Handover' },
  { key: 'bank_transfer', label: 'Bank Transfer' },
  { key: 'upi', label: 'UPI' },
]

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function CashPage() {
  const colors = useColors()
  const sCard = (accent: string): React.CSSProperties => ({
    flex: 1,
    minWidth: '180px',
    padding: '22px 20px',
    borderRadius: '16px',
    background: colors.bgCard,
    border: `1.5px solid ${accent}20`,
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
  })

  const sLabel: React.CSSProperties = {
    margin: '0 0 6px',
    fontSize: '11px',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  }

  const sValue = (color: string): React.CSSProperties => ({
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    color,
  })

  const sBadge = (bg: string, fg: string): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 12px',
    borderRadius: '999px',
    background: bg,
    color: fg,
    fontSize: '12px',
    fontWeight: 600,
  })

  const sBtn = (bg: string, fg: string): React.CSSProperties => ({
    padding: '8px 16px',
    borderRadius: '999px',
    border: 'none',
    background: bg,
    color: fg,
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: '40px',
  })

  const sTh: React.CSSProperties = {
    textAlign: 'left',
    padding: '12px 14px',
    fontSize: '11px',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bgSecondary,
  }

  const sTd: React.CSSProperties = {
    padding: '14px 14px',
    fontSize: '14px',
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.surface}`,
  }
  const [date, setDate] = useState(getToday())
  const [summary, setSummary] = useState<CashSummary | null>(null)
  const [history, setHistory] = useState<CashRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const [showDepositModal, setShowDepositModal] = useState(false)
  const depositRef = useModalA11y(showDepositModal, () => setShowDepositModal(false))
  const [depositTarget, setDepositTarget] = useState<string | null>(null)
  const [depositMethod, setDepositMethod] = useState('cash_handover')
  const [depositNotes, setDepositNotes] = useState('')
  const [depositing, setDepositing] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [summaryRes, historyRes] = await Promise.allSettled([
        adminApi.getCashSummary(date),
        adminApi.getCashHistory({ date_from: date, date_to: date, limit: 100 }),
      ])

      if (summaryRes.status === 'fulfilled') {
        const s = unwrap<CashSummary>(summaryRes.value)
        setSummary(s)
      }

      if (historyRes.status === 'fulfilled') {
        const h = unwrap<CashHistoryPayload>(historyRes.value)
        setHistory(Array.isArray(h?.records) ? h.records : Array.isArray(h?.collections) ? h.collections : Array.isArray(h) ? h : [])
      }
    } catch {
      setError('Unable to load cash data. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [date])

  useEffect(() => {
    void loadData()
  }, [loadData])

  // Realtime: when an agent collects cash, refresh the list so the new
  // collection appears and the COD-pending badge decrements without a
  // manual reload. Cheap reload - list is already date-filtered.
  // Note: the "Mark deposited" PATCH does NOT emit a socket event, so we
  // rely on the local optimistic refresh in handleDeposit() for that path.
  const handleCashCollectedSocket = useCallback(() => {
    void loadData()
  }, [loadData])

  useSocket({
    'order:cash_collected': handleCashCollectedSocket,
  })

  const handleToggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSelectAllPending = () => {
    const pendingIds = history.filter((r) => !r.deposited).map((r) => r.id)
    const allSelected = pendingIds.every((id) => selected.has(id))
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(pendingIds))
    }
  }

  const openDepositModal = (targetId: string | null) => {
    setDepositTarget(targetId)
    setDepositMethod('cash_handover')
    setDepositNotes('')
    setShowDepositModal(true)
  }

  const handleDeposit = async () => {
    setDepositing(true)
    try {
      if (depositTarget) {
        await adminApi.markCashDeposited(depositTarget, {
          deposited_method: depositMethod,
          notes: depositNotes || undefined,
        })
      } else {
        const ids = Array.from(selected)
        if (ids.length === 0) return
        await adminApi.markBulkDeposited(ids, depositMethod, depositNotes || undefined)
      }
      setShowDepositModal(false)
      setSelected(new Set())
      await loadData()
    } catch {
      setError('Unable to mark deposit. Please try again.')
    } finally {
      setDepositing(false)
    }
  }

  const pendingCount = history.filter((r) => !r.deposited).length

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          flexWrap: 'wrap',
          marginBottom: '28px',
        }}
      >
        <div>
          <h1
            className="font-display"
            style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: colors.textPrimary }}
          >
            Cash Reconciliation
          </h1>
          <p className="font-body" style={{ margin: '6px 0 0', fontSize: '14px', color: colors.textMuted }}>
            Track COD collections and agent deposits
          </p>
        </div>

        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="font-body"
          style={{
            height: '44px',
            borderRadius: '10px',
            border: `1.5px solid ${colors.border}`,
            padding: '0 14px',
            fontSize: '14px',
            color: colors.textPrimary,
            background: colors.bgCard,
          }}
        />
      </div>

      {/* Error */}
      {error ? <ErrorState message={error} onRetry={() => void loadData()} /> : null}

      {/* Summary cards */}
      {loading ? (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '28px' }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                ...sCard(colors.border),
                background: colors.bgSecondary,
                minHeight: '100px',
                animation: 'pulse 1.5s infinite',
              }}
            />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '28px' }}>
          <div style={sCard('#15803D')}>
            <p className="font-body" style={sLabel}>
              Total Collected
            </p>
            <p className="font-display" style={sValue('#15803D')}>
              ₹{formatCurrency(summary?.total_collected || 0)}
            </p>
          </div>
          <div style={sCard('#D6B97B')}>
            <p className="font-body" style={sLabel}>
              Deposited
            </p>
            <p className="font-display" style={sValue('#D6B97B')}>
              ₹{formatCurrency(summary?.total_deposited || 0)}
            </p>
          </div>
          <div style={sCard('#DC2626')}>
            <p className="font-body" style={sLabel}>
              Pending Deposit
            </p>
            <p className="font-display" style={sValue('#DC2626')}>
              ₹{formatCurrency(summary?.pending_deposit || 0)}
            </p>
          </div>
        </div>
      )}

      {/* Agent breakdown */}
      {!loading && summary?.by_agent && summary.by_agent.length > 0 ? (
        <div
          style={{
            background: colors.bgCard,
            borderRadius: '16px',
            border: `1.5px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: '28px',
          }}
        >
          <div style={{ padding: '18px 20px 12px' }}>
            <h3
              className="font-display"
              style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}
            >
              Agent-wise Breakdown
            </h3>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={sTh}>Agent</th>
                  <th style={sTh}>Orders</th>
                  <th style={sTh}>Collected</th>
                  <th style={sTh}>Deposited</th>
                  <th style={sTh}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {summary.by_agent.map((agent) => (
                  <tr key={agent.agent_id}>
                    <td style={{ ...sTd, fontWeight: 600 }}>{agent.agent_name || 'Agent'}</td>
                    <td style={sTd}>{agent.orders_count}</td>
                    <td style={{ ...sTd, fontWeight: 600, color: '#15803D' }}>
                      ₹{formatCurrency(agent.collected)}
                    </td>
                    <td style={{ ...sTd, color: '#D6B97B' }}>₹{formatCurrency(agent.deposited)}</td>
                    <td style={sTd}>
                      {agent.pending > 0 ? (
                        <span style={sBadge('rgba(220,38,38,0.08)', '#DC2626')}>
                          ₹{formatCurrency(agent.pending)}
                        </span>
                      ) : (
                        <span style={{ ...sBadge('rgba(21,128,61,0.08)', '#15803D'), gap: '6px' }}>
                          <Check size={14} strokeWidth={2.5} aria-hidden />
                          Clear
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Bulk action bar */}
      {selected.size > 0 ? (
        <div
          style={{
            position: 'sticky',
            bottom: '20px',
            background: colors.textPrimary,
            borderRadius: '16px',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '16px',
            boxShadow: '0px 8px 24px rgba(15,46,42,0.25)',
            zIndex: 20,
          }}
        >
          <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.surface }}>
            {selected.size} collection{selected.size > 1 ? 's' : ''} selected
          </p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              style={sBtn('transparent', colors.textMuted)}
            >
              Deselect
            </button>
            <button
              type="button"
              onClick={() => openDepositModal(null)}
              style={{ ...sBtn('#D6B97B', colors.textPrimary), display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <Check size={16} strokeWidth={2.5} aria-hidden />
              Mark as Deposited
            </button>
          </div>
        </div>
      ) : null}

      {/* Detailed cash history */}
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          border: `1.5px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 20px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <h3
            className="font-display"
            style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}
          >
            Collection Details
          </h3>
          {pendingCount > 0 ? (
            <button
              type="button"
              className="font-body"
              onClick={handleSelectAllPending}
              style={{
                ...sBtn('transparent', colors.textPrimary),
                border: `1.5px solid ${colors.border}`,
                fontSize: '12px',
                padding: '6px 14px',
                minHeight: '34px',
              }}
            >
              {selected.size > 0 && history.filter((r) => !r.deposited).every((r) => selected.has(r.id))
                ? 'Deselect All'
                : `Select All Pending (${pendingCount})`}
            </button>
          ) : null}
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
              Loading...
            </p>
          </div>
        ) : history.length === 0 ? (
          <EmptyState title="No cash records" description="No COD collections for this date." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...sTh, width: '40px' }} />
                  <th style={sTh}>Time</th>
                  <th style={sTh}>Order</th>
                  <th style={sTh}>Customer</th>
                  <th style={sTh}>Agent</th>
                  <th style={sTh}>Amount</th>
                  <th style={sTh}>Status</th>
                  <th style={sTh}>Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record) => (
                  <tr
                    key={record.id}
                    style={{
                      background: selected.has(record.id) ? 'rgba(214,185,123,0.06)' : 'transparent',
                    }}
                  >
                    <td style={{ ...sTd, textAlign: 'center' }}>
                      {!record.deposited ? (
                        <input
                          type="checkbox"
                          checked={selected.has(record.id)}
                          onChange={() => handleToggleSelect(record.id)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#D6B97B' }}
                        />
                      ) : null}
                    </td>
                    <td style={{ ...sTd, fontSize: '13px', color: colors.textMuted, whiteSpace: 'nowrap' }}>
                      {formatTime(record.created_at)}
                    </td>
                    <td style={{ ...sTd, fontFamily: 'Courier New, monospace', fontWeight: 600, fontSize: '13px' }}>
                      {record.order_number || '-'}
                    </td>
                    <td style={{ ...sTd, fontWeight: 500 }}>{record.customer_name || '-'}</td>
                    <td style={sTd}>{record.agent_name || '-'}</td>
                    <td style={{ ...sTd, fontWeight: 700, color: colors.textPrimary }}>
                      ₹{formatCurrency(record.amount)}
                    </td>
                    <td style={sTd}>
                      {record.deposited ? (
                        <span style={{ ...sBadge('rgba(21,128,61,0.08)', '#15803D'), gap: '6px' }}>
                          <Check size={14} strokeWidth={2.5} aria-hidden />
                          Deposited
                        </span>
                      ) : (
                        <span style={{ ...sBadge('rgba(217,119,6,0.08)', '#B45309'), gap: '6px' }}>
                          <Clock size={14} aria-hidden />
                          Pending
                        </span>
                      )}
                    </td>
                    <td style={sTd}>
                      {!record.deposited ? (
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => openDepositModal(record.id)}
                          style={{
                            ...sBtn('#15803D', colors.bgCard),
                            fontSize: '12px',
                            padding: '6px 14px',
                            minHeight: '32px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <Check size={14} strokeWidth={2.5} aria-hidden />
                          Mark Deposited
                        </button>
                      ) : (
                        <span
                          className="font-body"
                          style={{ fontSize: '12px', color: colors.textMuted }}
                        >
                          {record.deposited_method
                            ? record.deposited_method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                            : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deposit modal */}
      {showDepositModal ? (
        <>
          <button
            type="button"
            aria-label="Close modal"
            onClick={() => setShowDepositModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              zIndex: 90,
              cursor: 'default',
            }}
          />
          <div
            ref={depositRef}
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: colors.bgCard,
              borderRadius: '20px',
              padding: '28px',
              width: '420px',
              maxWidth: '92vw',
              zIndex: 100,
              boxShadow: '0px 20px 60px rgba(0,0,0,0.2)',
            }}
          >
            <h3
              className="font-display"
              style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}
            >
              Mark as Deposited
            </h3>
            <p className="font-body" style={{ margin: '0 0 20px', fontSize: '13px', color: colors.textMuted }}>
              {depositTarget
                ? 'Confirm this collection was deposited.'
                : `Mark ${selected.size} collection${selected.size > 1 ? 's' : ''} as deposited.`}
            </p>

            <label
              className="font-body"
              style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px' }}
            >
              Deposit Method
            </label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {DEPOSIT_METHODS.map((method) => (
                <button
                  key={method.key}
                  type="button"
                  className="font-body"
                  onClick={() => setDepositMethod(method.key)}
                  style={{
                    flex: 1,
                    minHeight: '44px',
                    borderRadius: '10px',
                    border: `1.5px solid ${depositMethod === method.key ? '#D6B97B' : colors.border}`,
                    background: depositMethod === method.key ? 'rgba(214,185,123,0.12)' : colors.bgCard,
                    fontSize: '13px',
                    fontWeight: depositMethod === method.key ? 700 : 500,
                    color: colors.textPrimary,
                    cursor: 'pointer',
                  }}
                >
                  {method.label}
                </button>
              ))}
            </div>

            <label
              className="font-body"
              style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px' }}
            >
              Notes (optional)
            </label>
            <textarea
              value={depositNotes}
              onChange={(e) => setDepositNotes(e.target.value)}
              placeholder="e.g., Txn ID, handover details..."
              className="font-body"
              rows={3}
              style={{
                width: '100%',
                borderRadius: '10px',
                border: `1.5px solid ${colors.border}`,
                padding: '12px 14px',
                fontSize: '14px',
                resize: 'vertical',
                marginBottom: '20px',
                boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                className="font-body"
                onClick={() => setShowDepositModal(false)}
                style={{
                  ...sBtn('transparent', colors.textPrimary),
                  border: `1.5px solid ${colors.border}`,
                  flex: 1,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="font-body"
                onClick={() => void handleDeposit()}
                disabled={depositing}
                style={{
                  ...sBtn('#15803D', colors.bgCard),
                  flex: 1,
                  opacity: depositing ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {depositing ? (
                  'Saving...'
                ) : (
                  <>
                    <Check size={16} strokeWidth={2.5} aria-hidden />
                    Confirm Deposit
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
